import { apiClient } from '@/api/client'
import type {
  AttivaPassPartout, AziendaFirma, AziendaFirmaRequest, CredenzialiFirma, DispositivoRequest, EsitoFirmaMultipla,
  FirmaAdmin, FirmaAssegnabile, FirmaImportRiepilogo, FirmaRequest, FirmaVoce, LimitiFirmaFile, MiaFirma, Sessione,
  StatoFirmaDigitale, VerificaCredenziali,
  FirmaVisibileRequest, ImmagineFirma, OpzioniFirmaVisibile, PosizioneRicordata,
} from '../types'

const BASE = '/api/v2/firma-remota'

/** Corpo `firma` delle richieste di protocollo: sessione oppure firma + dispositivo + OTP. */
export function corpoFirma(c: CredenzialiFirma): { firmaId?: number; sessioneId?: string; dispositivoId?: number; otp?: string } {
  return c.sessioneId !== undefined
    ? { sessioneId: c.sessioneId, firmaId: c.firmaId }
    : { firmaId: c.firmaId, dispositivoId: c.dispositivoId, otp: c.otp }
}

/** Tutte le chiamate dell'API di firma remota. Nessun dato sensibile resta qui: l'OTP passa solo di mano. */
export function useFirmaRemota() {
  // ── Uso: le firme che posso usare ──
  async function mia(): Promise<MiaFirma> {
    return (await apiClient.get<MiaFirma>(`${BASE}/mia`)).data
  }
  /**
   * Con `centroId` e `tipoProtocollo` (0 entrata, 1 uscita) il server dice anche se un protocollo così passerebbe dal
   * workflow di firma (`passaDallaFirma`): se no, la spunta «Firma digitale» non ha senso.
   */
  async function statoProtocollo(per?: { centroId?: number | null; tipoProtocollo?: number | null; conPec?: boolean }): Promise<StatoFirmaDigitale> {
    const params = per?.centroId != null && per.tipoProtocollo != null
      ? { centroId: per.centroId, tipoProtocollo: per.tipoProtocollo, conPec: per.conPec === true }
      : undefined
    return (await apiClient.get<StatoFirmaDigitale>('/api/v2/protocolli/firma-digitale/stato', { params })).data
  }

  // ── Sessione ──
  async function apriSessione(firmaId: number | undefined, dispositivoId: number, otp: string): Promise<Sessione> {
    return (await apiClient.post<Sessione>(`${BASE}/sessione`, { firmaId, dispositivoId, otp }, { timeout: 60000 })).data
  }
  /** La sessione in corso, o null (404 = nessuna sessione aperta o scaduta). */
  async function sessioneCorrente(): Promise<Sessione | null> {
    const r = await apiClient.get<Sessione>(`${BASE}/sessione`, { validateStatus: (s) => s === 200 || s === 404 })
    return r.status === 200 ? r.data : null
  }
  async function chiudiSessione(sessioneId: string): Promise<void> {
    await apiClient.delete(`${BASE}/sessione/${encodeURIComponent(sessioneId)}`, { validateStatus: (s) => s === 204 || s === 404 })
  }

  // ── Configurazione (FIRMA_REMOTA_ADMIN) ──
  async function azienda(): Promise<AziendaFirma> {
    return (await apiClient.get<AziendaFirma>(`${BASE}/azienda`)).data
  }
  /** Solo passpartout: attiva la firma per l'azienda del token e concede i permessi di configurazione a chi chiama. */
  async function attivaPassPartout(): Promise<AttivaPassPartout> {
    return (await apiClient.post<AttivaPassPartout>(`${BASE}/attiva-passpartout`)).data
  }
  async function salvaAzienda(r: AziendaFirmaRequest): Promise<AziendaFirma> {
    return (await apiClient.put<AziendaFirma>(`${BASE}/azienda`, r)).data
  }
  async function firme(): Promise<FirmaAdmin[]> {
    return (await apiClient.get<FirmaAdmin[]>(`${BASE}/firme`)).data
  }
  async function creaFirma(r: FirmaRequest): Promise<FirmaAdmin> {
    return (await apiClient.post<FirmaAdmin>(`${BASE}/firme`, r)).data
  }
  async function modificaFirma(id: number, r: FirmaRequest): Promise<FirmaAdmin> {
    return (await apiClient.put<FirmaAdmin>(`${BASE}/firme/${id}`, r)).data
  }
  async function eliminaFirma(id: number): Promise<void> {
    await apiClient.delete(`${BASE}/firme/${id}`)
  }
  async function aggiungiDispositivo(id: number, r: DispositivoRequest): Promise<FirmaAdmin> {
    return (await apiClient.post<FirmaAdmin>(`${BASE}/firme/${id}/dispositivi`, r)).data
  }
  async function modificaDispositivo(id: number, did: number, r: DispositivoRequest): Promise<FirmaAdmin> {
    return (await apiClient.put<FirmaAdmin>(`${BASE}/firme/${id}/dispositivi/${did}`, r)).data
  }
  async function eliminaDispositivo(id: number, did: number): Promise<FirmaAdmin> {
    return (await apiClient.delete<FirmaAdmin>(`${BASE}/firme/${id}/dispositivi/${did}`)).data
  }
  async function importaDispositivi(id: number): Promise<FirmaAdmin> {
    return (await apiClient.post<FirmaAdmin>(`${BASE}/firme/${id}/dispositivi/importa`)).data
  }
  async function verificaCredenziali(id: number): Promise<VerificaCredenziali> {
    return (await apiClient.post<VerificaCredenziali>(`${BASE}/firme/${id}/verifica-credenziali`)).data
  }
  /** Prova di firma: PDF facoltativo (se manca lo genera il server). Restituisce il PDF firmato. */
  async function provaFirma(id: number, dispositivoId: number, otp: string, pdf?: File | null): Promise<Blob> {
    const fd = new FormData()
    fd.append('dispositivoId', String(dispositivoId))
    fd.append('otp', otp)
    if (pdf) fd.append('file', pdf)
    return (await apiClient.post<Blob>(`${BASE}/firme/${id}/prova-firma`, fd, {
      responseType: 'blob', timeout: 150000, headers: { 'Content-Type': 'multipart/form-data' },
    })).data
  }

  // ── Esporta / importa la configurazione (senza password) ──
  /** Scarica il JSON della configurazione dell'azienda: nome dal Content-Disposition del server. */
  async function esportaConfigurazione(): Promise<{ blob: Blob; nome: string }> {
    const r = await apiClient.get<Blob>(`${BASE}/esporta`, { responseType: 'blob' })
    const cd = String(r.headers['content-disposition'] ?? '')
    const m = /filename\*=UTF-8''([^;]+)/i.exec(cd) ?? /filename="?([^";]+)"?/i.exec(cd)
    return { blob: r.data, nome: m?.[1] ? decodeURIComponent(m[1]) : 'firme-remote.json' }
  }
  /** `dryRun=true`: solo il riepilogo, nulla viene scritto. `false`: importa davvero. */
  async function importaConfigurazione(file: File, dryRun: boolean): Promise<FirmaImportRiepilogo> {
    const fd = new FormData()
    fd.append('file', file)
    return (await apiClient.post<FirmaImportRiepilogo>(`${BASE}/importa`, fd, {
      params: { dryRun }, headers: { 'Content-Type': 'multipart/form-data' },
    })).data
  }

  // ── Assegnazione dalla scheda del personale ──
  async function firmeDiPersona(personaleId: number): Promise<FirmaAssegnabile[]> {
    return (await apiClient.get<FirmaAssegnabile[]>(`${BASE}/personale/${personaleId}/firme`)).data
  }
  async function impostaFirmeDiPersona(personaleId: number, firmaIds: number[]): Promise<FirmaAssegnabile[]> {
    return (await apiClient.put<FirmaAssegnabile[]>(`${BASE}/personale/${personaleId}/firme`, { firmaIds })).data
  }

  // ── Protocolli ──
  async function firmaProtocolli(protocolloIds: number[], c: CredenzialiFirma): Promise<EsitoFirmaMultipla> {
    return (await apiClient.post<EsitoFirmaMultipla>('/api/v2/protocolli/firma-digitale',
      { protocolloIds, firma: corpoFirma(c) }, { timeout: 600000 })).data
  }
  async function firmeProtocollo(id: number): Promise<FirmaVoce[]> {
    return (await apiClient.get<FirmaVoce[]>(`/api/v2/protocolli/${id}/firma-digitale`)).data
  }

  // ── Utility «Firma documenti» ──
  async function limitiFirmaFile(): Promise<LimitiFirmaFile> {
    return (await apiClient.get<LimitiFirmaFile>(`${BASE}/firma-file/limiti`)).data
  }
  async function firmaFile(file: File[], c: CredenzialiFirma, formato: string | null): Promise<{ blob: Blob; nome: string }> {
    const fd = new FormData()
    for (const f of file) fd.append('file', f)
    if (c.firmaId !== undefined) fd.append('firmaId', String(c.firmaId))
    if (c.sessioneId !== undefined) fd.append('sessioneId', c.sessioneId)
    else { fd.append('dispositivoId', String(c.dispositivoId)); fd.append('otp', c.otp) }
    if (formato) fd.append('formato', formato)
    const r = await apiClient.post<Blob>(`${BASE}/firma-file`, fd, {
      responseType: 'blob', timeout: 600000, headers: { 'Content-Type': 'multipart/form-data' },
    })
    const cd = String(r.headers['content-disposition'] ?? '')
    const utf = /filename\*=UTF-8''([^;]+)/i.exec(cd)
    const semplice = /filename="?([^";]+)"?/i.exec(cd)
    const nome = utf?.[1] ? decodeURIComponent(utf[1]) : (semplice?.[1] ?? (file.length > 1 ? 'documenti-firmati.zip' : 'documento-firmato'))
    return { blob: r.data, nome }
  }

  // ── Firma PAdES visibile (Strumenti → Firma digitale) ──
  async function opzioniVisibile(): Promise<OpzioniFirmaVisibile> {
    return (await apiClient.get<OpzioniFirmaVisibile>(`${BASE}/visibile/opzioni`)).data
  }
  /** PNG dell'aspetto del riquadro (dati dell'utente corrente): esattamente quello che finisce nel PDF. */
  async function aspettoVisibile(p: {
    firmaId?: number; immagine: ImmagineFirma; motivo?: string; localita?: string; larghezza: number; altezza: number
  }): Promise<Blob> {
    return (await apiClient.get<Blob>(`${BASE}/visibile/aspetto`, {
      params: {
        firmaId: p.firmaId, immagine: p.immagine, motivo: p.motivo || undefined, localita: p.localita || undefined,
        larghezza: Math.round(p.larghezza * 100) / 100, altezza: Math.round(p.altezza * 100) / 100,
      },
      responseType: 'blob',
    })).data
  }
  /** Ultime posizioni usate per documenti con lo stesso nome o modello (nome → posizione). */
  async function posizioniRicordate(nomi: string[]): Promise<Record<string, PosizioneRicordata>> {
    if (!nomi.length) return {}
    const qs = new URLSearchParams()
    for (const n of nomi.slice(0, 50)) qs.append('nome', n)
    return (await apiClient.get<Record<string, PosizioneRicordata>>(`${BASE}/visibile/posizioni?${qs.toString()}`)).data
  }
  async function firmaVisibile(file: File[], richiesta: FirmaVisibileRequest, c: CredenzialiFirma): Promise<{ blob: Blob; nome: string }> {
    const fd = new FormData()
    for (const f of file) fd.append('file', f)
    fd.append('richiesta', JSON.stringify(richiesta))
    if (c.firmaId !== undefined) fd.append('firmaId', String(c.firmaId))
    if (c.sessioneId !== undefined) fd.append('sessioneId', c.sessioneId)
    else { fd.append('dispositivoId', String(c.dispositivoId)); fd.append('otp', c.otp) }
    const r = await apiClient.post<Blob>(`${BASE}/visibile/firma`, fd, {
      responseType: 'blob', timeout: 600000, headers: { 'Content-Type': 'multipart/form-data' },
    })
    return { blob: r.data, nome: nomeDaIntestazione(String(r.headers['content-disposition'] ?? ''), file.length > 1 ? 'documenti-firmati.zip' : 'documento-firmato.pdf') }
  }

  return {
    opzioniVisibile, aspettoVisibile, posizioniRicordate, firmaVisibile,
    mia, statoProtocollo, apriSessione, sessioneCorrente, chiudiSessione,
    azienda, attivaPassPartout, salvaAzienda, firme, creaFirma, modificaFirma, eliminaFirma,
    aggiungiDispositivo, modificaDispositivo, eliminaDispositivo, importaDispositivi,
    verificaCredenziali, provaFirma, firmeDiPersona, impostaFirmeDiPersona, esportaConfigurazione, importaConfigurazione,
    firmaProtocolli, firmeProtocollo, limitiFirmaFile, firmaFile,
  }
}

/** Nome del file dal Content-Disposition (forma UTF-8 o semplice). */
function nomeDaIntestazione(cd: string, ripiego: string): string {
  const utf = /filename\*=UTF-8''([^;]+)/i.exec(cd)
  const semplice = /filename="?([^";]+)"?/i.exec(cd)
  return utf?.[1] ? decodeURIComponent(utf[1]) : (semplice?.[1] ?? ripiego)
}

/** Scarica un Blob con il nome dato. */
export function scaricaBlob(blob: Blob, nome: string): void {
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = nome
  document.body.appendChild(a)
  a.click()
  a.remove()
  setTimeout(() => URL.revokeObjectURL(url), 10000)
}
